import { makeStyles } from '@material-ui/core/styles'
import { useState } from 'react'
import { Button as RAButton, FunctionField, Identifier, Link, sanitizeListRestProps } from 'react-admin'
import { FlexBox } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { usePerformanceScoreRanges } from 'shared/hooks/query/usePerformanceScoreRanges'
import { useSafetyScoreRanges } from 'shared/hooks/query/useSafetyScoreRanges'
import { SafetyScoreRange } from 'shared/models/compliance'
import { ScorePill } from 'shared/pages/ProfilePage/ProfileOverview/ScorePill'
import { darkGray, greyc4, primaryBlack, royalBlue } from 'shared/theme/colors'
import { header4 } from 'shared/theme/typography'
import styled from 'styled-components'

import { DOCUMENT_STATUS, SAFETY_TOOL_DOCUMENT_STATUS_CHOICES } from 'helpers/documents'
import { useHasEnhancedSafetyTool, useHasPerformanceScores } from '../../hooks/useHasFeatureFlag'
import { CTDatagrid } from '../CTDatagrid'
import { FullNameField, SimpleCalloutField } from '../Fields'
import CTList from '../List'
import { SafetyToolListFilter, StyledTopToolbar } from './SafetyToolListFilter'

const rowStyle = () => ({
  height: '70px',
})

const useStyles = makeStyles({
  configureCTDocsButton: {
    textTransform: 'none',
  },
})

const ListActions = ({
  currentSort,
  resource,
  filters,
  displayedFilters,
  filterValues,
  permanentFilter,
  basePath,
  onUnselectItems,
  showFilter,
  maxResults,
  total,
  ...rest
}: any) => {
  const classes = useStyles()
  const hasPerformanceScoresFlag = useHasPerformanceScores()

  if (hasPerformanceScoresFlag) {
    return (
      <StyledTopToolbar {...sanitizeListRestProps(rest)}>
        <Button variant="text" href="/compliance-documents-management" size="small">
          Configure Compliance Documents
        </Button>
      </StyledTopToolbar>
    )
  }

  return (
    <StyledTopToolbar {...sanitizeListRestProps(rest)}>
      <RAButton
        color="primary"
        href="/compliance-documents-management"
        label="Configure CT Documents"
        className={classes.configureCTDocsButton}
      />
    </StyledTopToolbar>
  )
}

export const InvalidDocuments = (props: any) => {
  const { record } = props
  const status = DOCUMENT_STATUS['EXPIRED']
  const numInvalidDocuments: number = record?.number_of_invalid_documents
    ? record?.number_of_invalid_documents
    : 0
  const hasInvalidDocs: boolean = numInvalidDocuments > 0
  return (
    <SimpleCalloutField
      styleProps={{
        style: {
          color: hasInvalidDocs ? status.color : greyc4,
          fontSize: 'medium',
          borderRadius: '3px',
          marginLeft: '7px',
        },
      }}
      label={hasInvalidDocs ? numInvalidDocuments : '-'}
      labelVariant="body1"
    />
  )
}
export const ExpiringDocuments = (props: any) => {
  const { record } = props
  const status = DOCUMENT_STATUS['EXPIRING']
  const numOfExpiringDocuments = record?.number_of_expiring_documents
    ? record?.number_of_expiring_documents
    : 0
  const hasExpiringDocs: boolean = numOfExpiringDocuments > 0
  return (
    <SimpleCalloutField
      styleProps={{
        style: {
          color: hasExpiringDocs ? status.color : greyc4,
          backgroundColor: hasExpiringDocs ? status.backgroundColor : 'transparent',
          fontSize: 'medium',
          borderRadius: '3px',
          marginLeft: '7px',
        },
      }}
      label={hasExpiringDocs ? numOfExpiringDocuments : '-'}
      labelVariant="body2"
    />
  )
}

export const ValidDocuments = (props: any) => {
  const { record } = props
  const status = DOCUMENT_STATUS['VALID']

  const numOfValidDocuments: number = record?.number_of_valid_documents
    ? record?.number_of_valid_documents
    : 0
  return (
    <SimpleCalloutField
      styleProps={{
        style: {
          color: status.color,
          backgroundColor: status.backgroundColor,
          fontSize: 'medium',
          borderRadius: '3px',
          marginLeft: '7px',
        },
      }}
      label={numOfValidDocuments}
      labelVariant="body2"
    />
  )
}

export const StatusCount = (props: any) => {
  const { record, selected_status, selected_types } = props
  const status = selected_status?.length ? DOCUMENT_STATUS[selected_status] : DOCUMENT_STATUS['VALID']
  const documents_statuses = record?.documents_statuses ? record?.documents_statuses : {}

  let num_of_docs_with_status = 0
  if ((!selected_status && !selected_types) || !documents_statuses) {
    return <></>
  }

  if (selected_types.length === 0) {
    num_of_docs_with_status =
      selected_status === 'EXPIRING'
        ? record?.number_of_expiring_documents
        : selected_status === 'EXPIRED'
        ? record?.number_of_expired_documents
        : selected_status === 'VALID'
        ? record?.number_of_valid_documents
        : selected_status === 'MISSING_DATE'
        ? record?.number_of_missing_expiration_documents
        : selected_status === 'NOT_UPLOADED'
        ? record?.number_of_missing_documents
        : 0
  } else {
    for (const document_type in documents_statuses) {
      if (documents_statuses[document_type] === selected_status) {
        num_of_docs_with_status++
      }
    }
  }
  return (
    <SimpleCalloutField
      styleProps={{
        style: {
          color: status?.color || 'transparent',
          backgroundColor: status?.backgroundColor || 'transparent',
          fontSize: 'medium',
          borderRadius: '3px',
          marginLeft: '7px',
        },
      }}
      label={num_of_docs_with_status}
      labelVariant="body2"
    />
  )
}

export const SafetyToolList = (props: any) => {
  const [documentStatuses, setDocumentStatuses] = useState<keyof typeof DOCUMENT_STATUS | undefined | ''>(
    undefined
  )
  const [types, setTypes] = useState<string[]>([])
  const status_name = SAFETY_TOOL_DOCUMENT_STATUS_CHOICES.find(
    (element) => element.id === documentStatuses
  )?.name

  const hasEnhancedSafetyToolFlag = useHasEnhancedSafetyTool()
  const hasPerformanceScoresFlag = useHasPerformanceScores()
  const { safetyScoreRanges } = useSafetyScoreRanges()
  const { performanceScoreRanges } = usePerformanceScoreRanges()

  if (hasPerformanceScoresFlag) {
    return (
      <FlexBox isColumn mt={4} mb={4}>
        <FlexBox isColumn gap="16px" mb={5}>
          <Text variant="header2">Safety & Performance</Text>

          <FlexBox gap="16px">
            {safetyScoreRanges.map((range: SafetyScoreRange, index: number) => (
              <FlexBox key={range.label} gap="16px" alignItems="center">
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: range.color,
                    borderRadius: '2px',
                    marginTop: '-4px',
                  }}
                />
                <Text key={index} variant="label2" fontColor={primaryBlack} as="span">
                  {range.label}
                </Text>
              </FlexBox>
            ))}
          </FlexBox>
        </FlexBox>
        <StyledCTList
          bulkActionButtons={false}
          filters={
            // @ts-ignore: List component clones element with additional props
            <SafetyToolListFilter setTypes={setTypes} setDocumentStatuses={setDocumentStatuses} />
          }
          actions={<ListActions />}
          sort={{ field: 'safety_score_v2', order: 'ASC' }}
          {...props}
        >
          <CTDatagrid
            optimized
            onRowClick={(userId: Identifier, basePath, recor, target) => {
              window.open(
                hasEnhancedSafetyToolFlag
                  ? `/compliance-safety-tool/${userId}`
                  : `/users/${userId}/show/documents`,
                target
              )
            }}
            rowStyle={rowStyle}
          >
            <FunctionField
              label="Driver"
              render={(record: any) =>
                hasEnhancedSafetyToolFlag ? (
                  <Text variant="header3" fontColor={darkGray}>
                    {record?.driver?.user?.first_name} {record?.driver?.user?.last_name}
                  </Text>
                ) : (
                  <FullNameField record={record?.driver?.user} />
                )
              }
            />
            <FunctionField
              label="CT Safety Score"
              render={(record: any | undefined) =>
                hasEnhancedSafetyToolFlag ? (
                  <ScorePill
                    score={record && record.safety_score_v2 !== null ? record.safety_score_v2 : null}
                    color={record?.safety_score_v2_color}
                    backgroundColor={record?.safety_score_v2_background_color}
                    size="medium"
                  />
                ) : (
                  record?.safety_score_v2?.toFixed(0) || ''
                )
              }
              sortBy="safety_score_v2"
            />
            <FunctionField
              label="Driving Behaviors"
              render={(record: any) => (
                <Text variant="header3">{record.driving_behavior_score?.toFixed(0)}</Text>
              )}
              sortBy="driving_behavior_score"
            />
            <FunctionField
              label="Accidents / Violations"
              render={(record: any) => (
                <Text variant="header3">{record.accidents_violations_score?.toFixed(0)}</Text>
              )}
              sortBy="accidents_violations_score"
            />
            <FunctionField
              label="Compliance"
              render={(record: any) => (
                <Text variant="header3">{record.on_road_compliance_score?.toFixed(0)}</Text>
              )}
              sortBy="on_road_compliance_score"
            />
            <FunctionField
              label="On-time service rate"
              render={(record: any) => (
                <ScorePill
                  score={record.on_time_service_score ? Math.floor(record.on_time_service_score * 100) : null}
                  color={
                    performanceScoreRanges.find((range) => record.on_time_service_score >= range.min)?.color
                  }
                  backgroundColor={
                    performanceScoreRanges.find((range) => record.on_time_service_score >= range.min)
                      ?.backgroundColor
                  }
                  isPercentage
                  size="medium"
                />
              )}
              sortBy="on_time_service_score"
            />
            {!documentStatuses ? (
              <InvalidDocuments
                label="Invalid Documents"
                source="number_of_invalid_documents"
                sortable={true}
              />
            ) : (
              <StatusCount
                label={status_name + ' Documents'}
                selected_status={documentStatuses}
                selected_types={types}
                source="number_of_invalid_documents"
                sortable={true}
              />
            )}
          </CTDatagrid>
        </StyledCTList>
      </FlexBox>
    )
  }

  return (
    <CTList
      bulkActionButtons={false}
      filters={
        // @ts-ignore: List component clones element with additional props
        <SafetyToolListFilter setTypes={setTypes} setDocumentStatuses={setDocumentStatuses} />
      }
      actions={<ListActions />}
      sort={{ field: 'safety_score_v2', order: 'ASC' }}
      {...props}
    >
      <CTDatagrid
        optimized
        onRowClick={(userId: Identifier, basePath, recor, target) => {
          window.open(
            hasEnhancedSafetyToolFlag
              ? `/compliance-safety-tool/${userId}`
              : `/users/${userId}/show/documents`,
            target
          )
        }}
        rowStyle={rowStyle}
      >
        <FunctionField
          label="Driver"
          render={(record: any) =>
            hasEnhancedSafetyToolFlag ? (
              <FlexBox>
                <Link
                  to={`/users/${record?.driver?.user?.id}/show#documents`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text variant="header3" fontColor={royalBlue}>
                    {record?.driver?.user?.first_name} {record?.driver?.user?.last_name}
                  </Text>
                </Link>
              </FlexBox>
            ) : (
              <FullNameField record={record?.driver?.user} />
            )
          }
        />
        <FunctionField
          label="CT Safety Score"
          render={(record: any) =>
            hasEnhancedSafetyToolFlag ? (
              <FlexBox gap="8px">
                <Text variant="label2" fontColor={primaryBlack} as="span">
                  {record.safety_score_v2?.toFixed(0)}
                </Text>
                <Text variant="label2" fontColor={record.safety_score_v2_color} as="span">
                  {record.safety_score_v2_label}
                </Text>
              </FlexBox>
            ) : (
              record.safety_score_v2?.toFixed(0)
            )
          }
          sortBy="safety_score_v2"
        />
        <FunctionField
          label="Driving Behaviors"
          render={(record: any) => {
            return record.driving_behavior_score?.toFixed(0)
          }}
          sortBy="driving_behavior_score"
        />
        <FunctionField
          label="Accidents / Violations"
          render={(record: any) => {
            return record.accidents_violations_score?.toFixed(0)
          }}
          sortBy="accidents_violations_score"
        />
        <FunctionField
          label="Compliance"
          render={(record: any) => {
            return record.on_road_compliance_score?.toFixed(0)
          }}
          sortBy="on_road_compliance_score"
        />

        <FunctionField
          label="Safety Dashboard"
          render={(record: any) => {
            const userId = record?.driver?.user?.id
            const url =
              userId && 'https://cloudtruckslooker.cloud.looker.com/dashboards/184?User+ID=' + userId
            return url ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                Safety Dashboard
              </a>
            ) : (
              ''
            )
          }}
        />
        {!documentStatuses ? (
          <InvalidDocuments label="Invalid Documents" source="number_of_invalid_documents" sortable={true} />
        ) : (
          <StatusCount
            label={status_name + ' Documents'}
            selected_status={documentStatuses}
            selected_types={types}
            source="number_of_invalid_documents"
            sortable={true}
          />
        )}
      </CTDatagrid>
    </CTList>
  )
}

const StyledCTList = styled(CTList)`
  .MuiTableCell-head {
    span {
      ${header4}
    }
  }
`
